// Script safety utilities to prevent third-party script issues
declare global {
  interface Window {
    React?: any;
  }
}

export const scriptSafety = {
  // Block problematic scripts that might cause React errors
  blockProblematicScripts: () => {
    console.log('🛡️ [SCRIPT-SAFETY] Initializing script blocking...')
    
    // Override console.error to catch and suppress React Error #130 from ads scripts
    const originalConsoleError = console.error
    console.error = function(...args: any[]) {
      const message = args[0]?.toString() || ''
      const stack = args[1]?.stack || new Error().stack || ''
      
      // Suppress React Error #130 from ads scripts
      if (message.includes('Minified React error #130') ||
          message.includes('ads.914af30a.js') ||
          stack.includes('ads.914af30a.js') ||
          stack.includes('ads.')) {
        console.warn('🚫 [SCRIPT-SAFETY] Suppressed ads script error:', message.substring(0, 100))
        // Prevent the error from propagating
        event?.preventDefault?.()
        return false
      }
      
      return originalConsoleError.apply(console, args)
    }

    // Block the ads script execution completely
    const scriptBlockObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement
            
            // Block script elements
            if (element.tagName === 'SCRIPT') {
              const script = element as HTMLScriptElement
              if (script.src && script.src.includes('ads.914af30a.js')) {
                console.warn('🚫 [SCRIPT-SAFETY] Blocked ads script:', script.src)
                script.remove()
                return
              }
            }
            
            // Also check for any child script elements
            const childScripts = element.querySelectorAll('script[src*="ads.914af30a.js"]')
            childScripts.forEach(script => {
              console.warn('🚫 [SCRIPT-SAFETY] Blocked child ads script:', script.getAttribute('src'))
              script.remove()
            })
          }
        })
      })
    })
    
    scriptBlockObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    })
    
    // Block script loading
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLScriptElement
            if (element.tagName === 'SCRIPT' && element.src) {
              if (element.src.includes('ads.914af30a.js') ||
                  element.src.includes('ads.') ||
                  element.src.includes('analytics.')) {
                console.warn('🚫 [SCRIPT-SAFETY] Blocked problematic script:', element.src)
                element.remove()
              }
            }
          }
        })
      })
    })
    
    observer.observe(document.documentElement, { 
      childList: true, 
      subtree: true 
    })
    
    console.log('🛡️ [SCRIPT-SAFETY] Script blocking activated')
  },

  // Clean up any existing problematic scripts
  cleanupExistingScripts: () => {
    const scripts = Array.from(document.querySelectorAll('script'))
    let removed = 0

    scripts.forEach(script => {
      if (script.src && (
        script.src.includes('ads.914af30a.js') ||
        script.src.includes('ads.') ||
        script.src.includes('analytics.') ||
        script.src.includes('tracker.')
      )) {
        console.warn('🧹 [SCRIPT-SAFETY] Removing existing problematic script:', script.src)
        script.remove()
        removed++
      }
    })

    if (removed > 0) {
      console.log(`🧹 [SCRIPT-SAFETY] Cleaned up ${removed} problematic scripts`)
    }
  },

  // Prevent React errors from ads scripts
  protectReact: () => {
    // Override global error handler
    const originalOnError = window.onerror
    window.onerror = function(message, source, lineno, colno, error) {
      const messageStr = message?.toString() || ''
      const sourceStr = source?.toString() || ''
      
      if ((messageStr.includes('React error #130') ||
           messageStr.includes('Minified React error') ||
           messageStr.includes('ads.914af30a.js')) &&
          (sourceStr.includes('ads.') || sourceStr.includes('analytics.') || sourceStr.includes('ads.914af30a.js'))) {
        console.warn('🚫 [SCRIPT-SAFETY] Suppressed React error from ads script')
        return true // Prevent default error handling
      }
      
      return originalOnError ? originalOnError.call(window, message, source, lineno, colno, error) : false
    }

    // Add addEventListener for error events
    window.addEventListener('error', function(event) {
      const message = event.message || event.error?.message || ''
      const filename = event.filename || ''
      const target = event.target as HTMLImageElement
      
      // Don't interfere with legitimate image loading for uploads
      if (target && target.tagName === 'IMG' && target.src &&
          (target.src.startsWith('blob:') || target.src.startsWith('data:'))) {
        console.log('🖼️ [SCRIPT-SAFETY] Allowing image validation for upload:', target.src.substring(0, 50))
        return false // Let the image validation handle this
      }
      
      if ((message.includes('React error #130') ||
           message.includes('Minified React error') ||
           filename.includes('ads.914af30a.js')) &&
          (filename.includes('ads.') || filename.includes('analytics.') || filename.includes('ads.914af30a.js'))) {
        console.warn('🚫 [SCRIPT-SAFETY] Prevented ads error event propagation')
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }, true) // Use capture phase
    
    // Override unhandled rejection handler
    const originalOnUnhandledRejection = window.onunhandledrejection
    window.onunhandledrejection = function(event) {
      const stack = event.reason?.stack || ''
      if (stack.includes('ads.') || stack.includes('analytics.')) {
        console.warn('🚫 [SCRIPT-SAFETY] Suppressed unhandled rejection from ads script')
        event.preventDefault()
        return
      }
      
      return originalOnUnhandledRejection ? originalOnUnhandledRejection.call(window, event) : undefined
    }
    
    console.log('🛡️ [SCRIPT-SAFETY] React protection activated')
  },

  // Initialize safety measures
  init: () => {
    console.log('🛡️ [SCRIPT-SAFETY] Initializing comprehensive script safety...')
    scriptSafety.cleanupExistingScripts()
    scriptSafety.blockProblematicScripts()
    scriptSafety.protectReact()
  }
}

// Auto-initialize if this file is imported
if (typeof window !== 'undefined') {
  // Initialize as early as possible
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scriptSafety.init)
  } else {
    scriptSafety.init()
  }
}